import { useEffect, useMemo, useState } from "react";
import { IDineInFloorTable, IDineInHistoryPage } from "../models/IDineIn";
import { dineInErrorCode, dineInErrorDescription, getDineInHistory, getDineInSalonHistory } from "../services/dineInService";
import HistoryBillItem, { formatDateTime, money } from "./HistoryBillItem";

type Preset = "today" | "yesterday" | "week" | "month" | "custom";

const PRESETS: { key: Preset; label: string }[] = [
  { key: "today", label: "Bugün" },
  { key: "yesterday", label: "Dün" },
  { key: "week", label: "Son 7 gün" },
  { key: "month", label: "Bu ay" },
  { key: "custom", label: "Özel" },
];

const PAGE_SIZE = 50;
const pad = (n: number) => String(n).padStart(2, "0");
const toDateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Seçilen ön ayarı yerel gün sınırlarına çevirir (tarayıcı saati = restoran saati varsayımı). */
function presetRange(preset: Preset, customFrom: string, customTo: string): { from: Date; to: Date } | null {
  const startOf = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const endOf = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
  const now = new Date();
  switch (preset) {
    case "today": return { from: startOf(now), to: endOf(now) };
    case "yesterday": { const y = new Date(now); y.setDate(now.getDate() - 1); return { from: startOf(y), to: endOf(y) }; }
    case "week": { const w = new Date(now); w.setDate(now.getDate() - 6); return { from: startOf(w), to: endOf(now) }; }
    case "month": return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOf(now) };
    case "custom": {
      if (!customFrom || !customTo) return null;
      return { from: startOf(new Date(`${customFrom}T00:00`)), to: endOf(new Date(`${customTo}T00:00`)) };
    }
  }
}

interface Props {
  restaurantId: string;
  /** Masa filtresi için salon görünümündeki masalar */
  tables: IDineInFloorTable[];
}

/** Bütün salonun (ya da seçilen masanın) sipariş geçmişi — kapanmış ve hâlâ açık hesaplar. */
export default function SalonHistoryTab({ restaurantId, tables }: Props) {
  const today = toDateValue(new Date());
  const [preset, setPreset] = useState<Preset>("today");
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);
  const [tableId, setTableId] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<IDineInHistoryPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const range = useMemo(() => presetRange(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const rangeInvalid = !range || range.to < range.from;

  useEffect(() => { setPage(1); }, [preset, customFrom, customTo, tableId]);

  useEffect(() => {
    if (rangeInvalid || !range) return;
    setLoading(true);
    setError("");
    const common = { fromUtc: range.from.toISOString(), toUtc: range.to.toISOString(), page, pageSize: PAGE_SIZE };
    const req = tableId
      ? getDineInHistory({ tableId, ...common })
      : getDineInSalonHistory({ restaurantId, ...common });
    req
      .then((res) => setData(res.data))
      .catch((err) => {
        setData(null);
        const code = dineInErrorCode(err);
        setError(code === "InvalidHistoryRange"
          ? (dineInErrorDescription(err) ?? "Tarih aralığı en fazla 92 gün olabilir.")
          : (dineInErrorDescription(err) ?? "Geçmiş yüklenemedi."));
      })
      .finally(() => setLoading(false));
  }, [restaurantId, tableId, range, rangeInvalid, page, reloadKey]);

  // Özet yüklü sayfa üzerinden hesaplanır; birden fazla sayfa varsa bu belirtilir.
  const summary = useMemo(() => {
    const items = data?.items ?? [];
    const paid = items.filter((i) => i.session.status === "Paid");
    const byMethod = new Map<string, { label: string; total: number; count: number }>();
    paid.forEach((i) => {
      const label = i.session.paymentMethodNameTr ?? "Diğer";
      const cur = byMethod.get(label) ?? { label, total: 0, count: 0 };
      cur.total += i.totalAmount;
      cur.count += 1;
      byMethod.set(label, cur);
    });
    return {
      paidCount: paid.length,
      paidTotal: paid.reduce((s, i) => s + i.totalAmount, 0),
      openCount: items.filter((i) => i.session.status === "Open").length,
      cancelledCount: items.filter((i) => i.session.status === "Cancelled").length,
      methods: Array.from(byMethod.values()),
      currency: items.find((i) => i.session.currencyDisplayText)?.session.currencyDisplayText,
    };
  }, [data]);

  const sortedTables = useMemo(() => tables.slice().sort((a, b) => a.tableNumber - b.tableNumber), [tables]);

  return (
    <div>
      {/* Filtreler */}
      <div className="dinein-history-filters" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {PRESETS.map((p) => (
            <button key={p.key} type="button"
              className={`dinein-btn ${preset === p.key ? "dinein-btn--primary" : "dinein-btn--ghost"}`}
              onClick={() => setPreset(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input type="date" className="form-control" style={{ width: "auto" }} value={customFrom} max={today}
              onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="dinein-muted">–</span>
            <input type="date" className="form-control" style={{ width: "auto" }} value={customTo} min={customFrom} max={today}
              onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}
        <select className="form-select" style={{ width: "auto", minWidth: 170 }} value={tableId} onChange={(e) => setTableId(e.target.value)}>
          <option value="">Tüm masalar</option>
          {sortedTables.map((t) => (
            <option key={t.tableId} value={t.tableId}>Masa {t.tableNumber}{t.label ? ` — ${t.label}` : ""}</option>
          ))}
        </select>
        <button type="button" className="dinein-btn dinein-btn--ghost" onClick={() => setReloadKey((k) => k + 1)} disabled={loading} title="Yenile">
          <i className={`bi bi-arrow-clockwise${loading ? " dinein-spin" : ""}`}></i>
        </button>
      </div>

      {preset === "custom" && rangeInvalid && (
        <div className="dinein-alert dinein-alert--warn"><i className="bi bi-exclamation-triangle-fill"></i><span>Bitiş tarihi başlangıçtan önce olamaz.</span></div>
      )}

      {/* Özet */}
      {data && data.items.length > 0 && (
        <div className="dinein-summary">
          <div className="dinein-summary__item dinein-summary__item--total">
            <span>Ödenen ({summary.paidCount})</span><strong>{money(summary.paidTotal, summary.currency)}</strong>
          </div>
          {summary.methods.map((m) => (
            <div key={m.label} className="dinein-summary__item">
              <span>{m.label} ({m.count})</span><strong>{money(m.total, summary.currency)}</strong>
            </div>
          ))}
          <div className="dinein-summary__item dinein-summary__item--busy">
            <span>Hâlâ açık</span><strong>{summary.openCount}</strong>
          </div>
          <div className="dinein-summary__item">
            <span>Boşaltılan</span><strong>{summary.cancelledCount}</strong>
          </div>
        </div>
      )}
      {data && data.totalPages > 1 && (
        <div className="dinein-small dinein-muted" style={{ margin: "-4px 0 10px" }}>
          <i className="bi bi-info-circle me-1"></i>Özet yalnızca bu sayfadaki {data.items.length} oturumu kapsar (toplam {data.totalCount}).
        </div>
      )}

      {/* Liste */}
      {loading && !data ? (
        <div className="dinein-center dinein-center--tall"><span className="spinner-border" /></div>
      ) : error ? (
        <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
      ) : !data || data.items.length === 0 ? (
        <div className="dinein-center dinein-center--tall">
          <i className="bi bi-inbox"></i>
          <div>Bu dönemde kayıt yok.</div>
        </div>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }}>
          <div className="dinein-small dinein-muted" style={{ marginBottom: 8 }}>
            {formatDateTime(data.fromUtc)} – {formatDateTime(data.toUtc)} · {data.totalCount} oturum · en yeni üstte
          </div>
          <div className="dinein-history-list">
            {data.items.map((item) => (
              <HistoryBillItem
                key={item.session.orderCode}
                item={item}
                showTable={!tableId}
                expanded={expanded === item.session.orderCode}
                onToggle={() => setExpanded(expanded === item.session.orderCode ? null : item.session.orderCode)}
              />
            ))}
          </div>

          {data.totalPages > 1 && (
            <div className="dinein-pagination">
              <button className="dinein-btn dinein-btn--ghost" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <span>{data.page} / {data.totalPages}</span>
              <button className="dinein-btn dinein-btn--ghost" disabled={page >= data.totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
